import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const categoryIcons: Record<string, string> = {
    shoes: 'ri-footprint-line',
    hoodies: 'ri-t-shirt-line',
    tshirts: 'ri-t-shirt-line',
    jackets: 'ri-shirt-line',
    pants: 'ri-slideshow-line',
    headwear: 'ri-base-station-line',
    sets: 'ri-stack-line',
    underwear: 'ri-shirt-line',
    jersey: 'ri-football-line',
    accessories: 'ri-glasses-line',
    bags: 'ri-briefcase-line',
    belts: 'ri-contrast-line',
    perfumes: 'ri-drop-line',
    other: 'ri-more-line',
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-background-50/95 backdrop-blur-sm shadow-sm' : 'bg-transparent'}`}>
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-leaf-line text-primary-500 text-xl"></i>
              </div>
              <span className="text-foreground-950 font-heading text-lg font-semibold tracking-tight group-hover:text-primary-600 transition-colors">
                KakobuyFinds
              </span>
            </a>

            <div className="hidden md:flex items-center gap-10">
              <a href="/" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                {t('nav.home')}
              </a>
              <a href="#categories" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                {t('nav.finds')}
              </a>
              <Link to="/artykul-kakobuy-sheet" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                {t('nav.blog')}
              </Link>
              <Link to="/cssbuy-spreadsheet" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                CSSBuy
              </Link>
              <Link to="/poradnik-bezpieczenstwa" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                Bezpieczeństwo
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-700 text-sm font-medium hover:text-primary-600 transition-colors whitespace-nowrap">
                Kakobuy Sheet
              </a>
            </div>

            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('hero.cta.buy')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </a>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden relative w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer rounded-full"
                aria-label="Menu"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-background-950/60 backdrop-blur-sm" onClick={() => setIsMenuOpen(false)}></div>
          <div className="absolute top-0 right-0 w-[85vw] max-w-sm h-full bg-background-50 overflow-y-auto animate-slide-in">
            <div className="pt-20 pb-8 px-5">
              <div className="mb-8">
                <h3 className="text-foreground-400 text-xs font-semibold tracking-widest uppercase mb-4">
                  {t('nav.menu.categories')}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-200 transition-colors">
                        <i className={`${categoryIcons[cat.id] || 'ri-price-tag-3-line'} text-sm`}></i>
                      </div>
                      <span className="text-foreground-700 text-xs font-medium whitespace-nowrap">
                        {t(cat.nameKey)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-foreground-400 text-xs font-semibold tracking-widest uppercase mb-4">
                  {t('nav.menu.articles')}
                </h3>
                <div className="space-y-1">
                  <Link
                    to="/artykul-kakobuy-sheet"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-file-text-line text-sm"></i>
                    </div>
                    <span className="text-foreground-700 text-sm font-medium">Kakobuy Spreadsheet – Przewodnik</span>
                  </Link>
                  <Link
                    to="/cssbuy-spreadsheet"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-scales-3-line text-sm"></i>
                    </div>
                    <span className="text-foreground-700 text-sm font-medium">CSSBuy – Poradnik i porównanie</span>
                  </Link>
                  <Link
                    to="/superbuy-spreadsheet"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-line-chart-line text-sm"></i>
                    </div>
                    <span className="text-foreground-700 text-sm font-medium">Superbuy – Pełna analiza</span>
                  </Link>
                  <Link
                    to="/poradnik-bezpieczenstwa"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                      <i className="ri-shield-check-line text-sm"></i>
                    </div>
                    <span className="text-foreground-700 text-sm font-medium">Bezpieczeństwo – Poradnik</span>
                  </Link>
                </div>
              </div>

              <div className="mb-8">
                <h3 className="text-foreground-400 text-xs font-semibold tracking-widest uppercase mb-4">
                  {t('nav.menu.external')}
                </h3>
                <div className="space-y-2">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full bg-foreground-900 hover:bg-foreground-950 text-white text-sm font-medium px-6 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Kakobuy Sheet
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium px-6 py-3.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Buy It Now
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shopping-bag-line"></i>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}